/**
 * View component for /admin/authoring/scenes/:sceneId
 *
 * Displays a single scene from the 'byId' map in the scene reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import renderHTML from 'react-render-html';

// import actions
import * as courseActions from '../../course/courseActions';
import * as nodeActions from '../../node/nodeActions';
import * as resourceLinkActions from '../../resourceLink/resourceLinkActions';
import * as sceneActions from '../sceneActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";
import PDFViewer from '../../../global/components/helpers/PDFViewer.js.jsx';

// import module components
import AdminResourceLinkListItem from '../../resourceLink/components/AdminResourceLinkListItem.js.jsx';
import ResourceLinkModal from '../../resourceLink/components/ResourceLinkModal.js.jsx';

// import form components
import SelectFromObject from '../../../global/components/forms/SelectFromObject.js.jsx';

// import utilities
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import scene css modules
import sceneStyles from '../sceneModuleStyles.css';

class AdminSingleScene extends Base {
  constructor(props) {
    super(props);
    this.state = {
      createLinkModalOpen: false
      , selectedContentModuleId: null
      , showDescription: false
      , showPreviewOptions: false
    }
    this._bind(
      '_handleFormChange'
      , '_onVideoComplete'
      , `_togglePreviewOptions`
    )
  }

  componentDidMount() {
    const { dispatch, match, sceneId } = this.props;
    dispatch(sceneActions.fetchSingleIfNeeded(sceneId ? sceneId : match.params.sceneId));
    dispatch(resourceLinkActions.fetchListIfNeeded('_scene', sceneId ? sceneId : match.params.sceneId));
    dispatch(nodeActions.fetchListIfNeeded('_scene', sceneId ? sceneId : match.params.sceneId)).then(nodeAction => {
      let contentModulesIds = nodeAction.list && nodeAction.list.length > 0 ? nodeAction.list.map(node => node._contentModule) : null;
      dispatch(contentModuleActions.fetchListIfNeeded('_id', contentModulesIds))
      dispatch(courseActions.fetchListIfNeeded('_contentModules', contentModulesIds))
    });
  }

  componentWillReceiveProps(nextProps) {
    const { dispatch, match, sceneId } = nextProps;
    dispatch(resourceLinkActions.fetchListIfNeeded('_scene', sceneId ? sceneId : match.params.sceneId));
  }

  _onVideoComplete() {
    console.log("VIDEO COMPLETED");
  }

  _togglePreviewOptions() {
    this.setState({
      showPreviewOptions: !this.state.showPreviewOptions
    });
  }

  _handleFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */

    let newState = _.update(this.state, e.target.name, function () {
      return e.target.value;
    });
    this.setState(newState);
  }

  render() {
    const {
      courseStore
      , location
      , match
      , nodeStore
      , resourceLinkStore
      , sceneId
      , sceneStore
      , contentModuleStore
    } = this.props;
    const { showPreviewOptions, selectedContentModuleId } = this.state;
    /**
     * use the selected.getItem() utility to pull the actual scene object from the map
     */
    const selectedScene = sceneStore.selected.getItem();
    const resourceLinks = resourceLinkStore.util.getList('_scene', sceneId ? sceneId : match.params.sceneId);
    const nodeList = nodeStore.util.getList('_scene', sceneId ? sceneId : match.params.sceneId);
    const contentModuleIds = nodeList ? nodeList.map(node => node._contentModule) : null;
    const contentModuleList = contentModuleStore.util.getList('_id', contentModuleIds)
    const courseList = courseStore.util.getList('_contentModules', contentModuleIds)

    // build links
    const courseLinks = courseList ?
      (courseList.map((course, i) => {
        return (
          <Link to={`/admin/authoring/courses/${course._id}`} target={'_blank'} key={course._id + i}>
            <p>{`Course: ${course.title} `}<i className="fa fa-external-link" /></p>
          </Link>
        )
      })
      )
      :
      null

    const cmLinks = contentModuleList ?
      (contentModuleList.map((cm, i) => {
        return (
          <Link to={`/admin/authoring/content-modules/${cm._id}`} target={'_blank'} key={cm._id + i}>
            <p><b>{`Content Module: ${cm.title} `}<i className="fa fa-external-link" /></b></p>
          </Link>
        )
      })
      )
      :
      null

    let warningText = null
    if ((cmLinks && cmLinks.length > 1) || (courseLinks && courseLinks.length > 1)) {
      warningText = 'WARNING: This scene is associated with more than one ContentModule or Course. Be careful making any edits.'
    }

    const isEmpty = (
      !selectedScene
      || !selectedScene._id
      || sceneStore.selected.didInvalidate
    );

    const isFetching = (
      courseStore.lists && courseStore.lists._contentModules && courseStore.lists._contentModules[contentModuleIds] && courseStore.lists._contentModules[contentModuleIds].isFetching
      || sceneStore.isFetching
      || contentModuleStore.lists && contentModuleStore.lists._id && contentModuleStore.lists._id[contentModuleIds] && contentModuleStore.lists._id[contentModuleIds].isFetching
    )

    const noLinks = (
      !resourceLinks
      || resourceLinks.length < 1
    )

    const playlist = selectedScene ? videoUtils.returnPlaylist(selectedScene.videoPath, selectedScene.posterPath, selectedScene.tracks) : [];

    let descriptionHTML = null;
    if (selectedScene && selectedScene.description && selectedScene.description.blocks && selectedScene.description.blocks.length > 0) {
      descriptionHTML = richContentUtils.editorState(selectedScene.description);
    }
    let transcriptHTML = null;
    if (selectedScene && selectedScene.transcript && selectedScene.transcript.blocks && selectedScene.transcript.blocks.length > 0) {
      transcriptHTML = richContentUtils.editorState(selectedScene.transcript);
    }

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section" >
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              {isEmpty ?
                (isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                  <div style={{ opacity: sceneStore.isFetching ? 0.5 : 1 }}>
                    <div className="yt-row with-gutters">
                      <div className="yt-col full s_60 m_70">
                        <div className="content-container">
                          <div className="yt-row space-between">
                            <p><strong>Scene details</strong></p>
                            <Link className="yt-btn x-small info" to={`/admin/authoring/scenes/${selectedScene._id}/update`}>Update scene details</Link>
                          </div>
                          {warningText}
                          <hr />
                          {selectedScene.videoPath ?
                            <VideoPlayer
                              isAutoPlay={false}
                              onVideoComplete={this._onVideoComplete}
                              playerId={selectedScene._id}
                              playlist={playlist}
                            />
                            :
                            selectedScene.pdfPath ?
                            <PDFViewer
                              styles={'pdf-preview'}
                              file={selectedScene.pdfPath}
                            />
                            :
                            null
                          }
                          <small>
                            <strong>Title</strong>
                          </small>
                          <p>{selectedScene.title}</p>
                          <small>
                            <strong>Overlay Text</strong>
                          </small>
                          <p>{selectedScene.overlayText}</p>
                          {selectedScene.videoPath ?
                            <div>
                              <small>
                                <strong>Show Transparent Overlay</strong>
                              </small>
                              <p>{selectedScene.transparentOverlay ? 'Yes' : 'No'}</p>
                            </div>
                          : null
                          }
                          <small>
                            <strong>Follow Up Question</strong>
                          </small>
                          <p>{selectedScene.followupQuestion}</p>
                          <small>
                            <strong>Summary</strong>
                          </small>
                          <p>{selectedScene.summary}</p>
                          <hr />
                          <br />
                          <small>
                            <strong>Detailed description</strong>
                          </small>
                          {descriptionHTML ? renderHTML(descriptionHTML) : null}
                          <hr />
                          <br />
                          <small>
                            <strong>Script</strong>
                          </small>
                          <div
                            style={{ whiteSpace: 'pre-line' }}
                          >
                            {selectedScene ? selectedScene.script : ''}
                          </div>
                          <hr />
                          <br />
                          <small>
                            <strong>Transcript</strong>
                          </small>
                          {transcriptHTML ? renderHTML(transcriptHTML) : null}
                        </div>
                      </div>
                      <div className="yt-col full s_40 m_30">
                        <div className="content-container">
                          <Link
                            className="yt-btn x-small warning"
                            to={`/admin/audit-logs/scene/${selectedScene._id}`}
                            target="_blank"
                          >
                            Object History &rarr;
                          </Link>
                          <p><strong>Resource Links</strong></p>
                          <div className="resource-links">
                            {noLinks ?
                              <em> No resource links yet </em>
                              :
                              resourceLinks.map((rLink, i) =>
                                <AdminResourceLinkListItem
                                  dispatch={this.props.dispatch}
                                  key={i}
                                  refKey="_scene"
                                  refId={rLink._scene}
                                  resourceLink={rLink}
                                />
                              )
                          }
                        </div>
                        <button className="yt-btn x-small success link" onClick={() => this.setState({ createLinkModalOpen: true })}><i className="fas fa-plus" /> Add Resource</button>
                      </div>
                      <div className="content-container">
                        <p><strong>Scene Usage</strong></p>
                        {cmLinks}
                        <hr />
                        {courseLinks}
                      </div>
                      </div>
                    </div>
                    <ResourceLinkModal
                      closeModal={() => this.setState({ createLinkModalOpen: false })}
                      dispatch={this.props.dispatch}
                      formType="Create"
                      isOpen={this.state.createLinkModalOpen}
                      refId={selectedScene._id}
                      refKey="_scene"
                      resourceLink={resourceLinkStore.defaultItem}
                    />
                  </div>
              }
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

AdminSingleScene.propTypes = {
  dispatch: PropTypes.func.isRequired
  , sceneId: PropTypes.string
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseStore: store.course
    , nodeStore: store.node
    , resourceLinkStore: store.resourceLink
    , sceneStore: store.scene
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleScene)
);



// WEBPACK FOOTER //
// modules/scene/views/AdminSingleScene.js.jsx